import { DATABASE_LARGE_DATA_POLICY } from '@streamlain/database-identity';

import type { DatabaseAttachment } from '../components/ai-chat-content/type';

type RowSelection = {
  rowId?: string;
  rows?: Array<{ id: string }>;
  selectionType: 'row';
  type: string;
};

type AreaSelection = {
  columnsSelection: { end: number; start: number };
  rowsSelection: { end: number; start: number };
  selectionType: 'area';
  type: 'table';
};

/**
 * Pure semantic boundary between BlockSuite selection objects and Copilot
 * database context. It intentionally has no BlockSuite runtime imports.
 */
export function applyDatabaseRowSelectionScope(
  attachment: DatabaseAttachment,
  selection: unknown
): DatabaseAttachment {
  if (!isRowSelection(selection) && !isAreaSelection(selection)) {
    return attachment;
  }

  const selectedRowIds = isAreaSelection(selection)
    ? selectedAreaRowIds(attachment, selection.rowsSelection)
    : selection.type === 'gantt'
      ? selection.rowId
        ? [selection.rowId]
        : []
      : (selection.rows?.map(row => row.id) ?? []);
  if (
    selectedRowIds.length >
    DATABASE_LARGE_DATA_POLICY.ai.contextSelectedRowLimit
  ) {
    throw new RangeError(
      `Database AI context supports at most ${DATABASE_LARGE_DATA_POLICY.ai.contextSelectedRowLimit} explicitly selected rows; ${selectedRowIds.length} were selected. No context was sent.`
    );
  }
  if (selectedRowIds.length === 0) return attachment;

  return {
    ...attachment,
    scope: 'selected-rows',
    selectedRowIds,
    selectedRowLabel: `${selectedRowIds.length} selected rows`,
    ...(isAreaSelection(selection)
      ? {
          projectedColumnIds: selectedAreaColumnIds(
            attachment,
            selection.columnsSelection
          ),
        }
      : {}),
  };
}

function isRowSelection(selection: unknown): selection is RowSelection {
  if (!isRecord(selection) || selection.selectionType !== 'row') {
    return false;
  }
  if (typeof selection.type !== 'string') {
    return false;
  }
  if (selection.type === 'gantt') {
    return typeof selection.rowId === 'string';
  }
  return (
    Array.isArray(selection.rows) &&
    selection.rows.every(row => isRecord(row) && typeof row.id === 'string')
  );
}

function isAreaSelection(selection: unknown): selection is AreaSelection {
  return (
    isRecord(selection) &&
    selection.type === 'table' &&
    selection.selectionType === 'area' &&
    isIndexRange(selection.rowsSelection) &&
    isIndexRange(selection.columnsSelection)
  );
}

function isIndexRange(value: unknown): value is { end: number; start: number } {
  return (
    isRecord(value) &&
    typeof value.start === 'number' &&
    Number.isFinite(value.start) &&
    typeof value.end === 'number' &&
    Number.isFinite(value.end)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function selectedAreaRowIds(
  attachment: DatabaseAttachment,
  selection: { start: number; end: number }
): string[] {
  const window = attachment.visibleRowWindow;
  if (!window) return [];
  const start = Math.max(
    0,
    Math.min(selection.start, selection.end) - window.offset
  );
  const end = Math.max(
    0,
    Math.max(selection.start, selection.end) - window.offset
  );
  return window.rowIds.slice(start, end + 1);
}

function selectedAreaColumnIds(
  attachment: DatabaseAttachment,
  selection: { start: number; end: number }
): string[] {
  const start = Math.max(0, Math.min(selection.start, selection.end));
  const end = Math.max(0, Math.max(selection.start, selection.end));
  const selectedViewColumnIds = attachment.columns
    .slice(start, end + 1)
    .map(column => column.id);
  const materializedColumnIds = attachment.projectedColumnIds
    ? new Set(attachment.projectedColumnIds)
    : undefined;

  return materializedColumnIds
    ? selectedViewColumnIds.filter(columnId =>
        materializedColumnIds.has(columnId)
      )
    : selectedViewColumnIds;
}
